<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faArrowDown, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
  import { Button } from 'flowbite-svelte';
  import CustomImage from '../../common/CustomImage.svelte';
  import { createGrid } from 'ag-grid-community';
  import type { GridApi } from 'ag-grid-community';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-quartz.css';
  import { theme } from '../../../services/stores/theme-store.js';
  import { ColumnHeaderForSource } from './headerComponents.js';
  import { addColumn, removeColumn, addRow, removeRow, resetToInitial, pivotTable, workingTableTo2DArray } from './source-table-processing.js';
  import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';

  // Props - Data
  export let availableSourceTables: any[] = [];
  export let selectedSourceTable: any | null = null;
  export let sourceData2D: any[][] = [];
  export let workingData: any = null;
  export let currentTableIndex: number = 0;
  export let effectiveTableId: string = '';
  export let isReferenceMode: boolean = false;
  export let topicId: string = '';
  export let referenceId: string = '';
  export let projectStructureStore: any = null;
  
  // Props - State
  export let columnAddEnabled: boolean = false;
  export let rowRemoveEnabled: boolean = false;
  export let selectedButtonEnabled: boolean = false;
  export let matchingButtonEnabled: boolean = false;
  export let selectedRows: Set<number> = new Set();
  export let selectedColumns: Set<number> = new Set();
  export let sourceGridInitialized: boolean = false;
  export let isUpdatingCell: boolean = false;
  export let show: boolean = false;
  
  // Props - Functions (passed from parent)
  export let convertToGridFormat: ((data2D: any[][], includeCheckboxes: boolean, isTargetGrid: boolean) => any) | undefined = undefined;
  export let getCurrentVersion: ((workingData: any, currentTableIndex: number, effectiveTableId: string) => any) | undefined = undefined;
  export let updateWorkingDataFromGrid: ((params: any) => void) | undefined = undefined;
  export let runValidation: (() => void) | undefined = undefined;
  
  // Props - Event handlers
  export let onSourceTableChange: ((table: any) => void) | undefined = undefined;
  export let onAddColumn: ((e: MouseEvent) => void) | undefined = undefined;
  export let onAddRow: (() => void) | undefined = undefined;
  export let onRemoveRow: (() => void) | undefined = undefined;
  export let onPivotTable: (() => void) | undefined = undefined;
  export let onResetTable: (() => void) | undefined = undefined;
  export let onMatchSelected: (() => void) | undefined = undefined;
  export let onMatchOnly: (() => void) | undefined = undefined;
  export let onMatchAll: (() => void) | undefined = undefined;
  export let onSelectionChanged: ((selectedRows: Set<number>) => void) | undefined = undefined;
  export let onGridReady: ((api: GridApi) => void) | undefined = undefined;
  export let onSaveColumn: ((columnName: string, columnKey: string, mode: 'add' | 'edit', columnIndex: number) => void) | undefined = undefined;
  export let onRemoveColumn: ((columnIndex: number) => void) | undefined = undefined;
  export let onSaveWorkingData: (() => void) | undefined = undefined;
  export let onRefreshSourceData: (() => void) | undefined = undefined;
  
  // Internal state
  let sourceGridDiv: HTMLDivElement;
  let sourceGridApi: GridApi | null = null;
  let sourceGridInstance: any = null;
  let hasChanges = false;
  
  // Column popup state
  let showColumnPopup = false;
  let popupMode: 'add' | 'edit' = 'add';
  let columnName = '';
  let columnKey = '';
  let currentColumnIndex = -1;
  let availableKeys: string[] = [];
  let popupPosition = { top: 0, left: 0 };
  
  // Initialize source grid when data is ready
  $: if (show && sourceGridDiv && sourceData2D.length > 0 && !sourceGridInitialized && selectedSourceTable && convertToGridFormat) {
    setTimeout(() => {
      initializeSourceGrid();
    }, 0);
  }
  
  function initializeSourceGrid() {
      if (!sourceGridDiv || !convertToGridFormat) {
      return;
    }
    
    // Destroy existing grid
    if (sourceGridInstance) {
      sourceGridInstance.destroy();
      sourceGridInstance = null;
      sourceGridApi = null;
    }
    
    // Create a custom convertToGridFormat that includes column popup handlers
    // We need to wrap the parent's convertToGridFormat to inject our popup handlers
    const customConvertToGridFormat = (data2D: any[][], includeCheckboxes: boolean, isTargetGrid: boolean) => {
      if (!convertToGridFormat) {
        return { columnDefs: [], rowData: [] };
      }
      
      const result = convertToGridFormat(data2D, includeCheckboxes, isTargetGrid);
      
      // Override onEditColumn and onDeleteColumn in columnDefs
      if (result.columnDefs) {
        result.columnDefs.forEach((colDef: any) => {
          if (colDef.headerComponentParams) {
            colDef.headerComponentParams.onEditColumn = (colIndex: number, event?: MouseEvent) => {
              selectedColumns.clear();
              selectedColumns.add(colIndex);
              openColumnPopup('edit', colIndex, event);
            };
            colDef.headerComponentParams.onDeleteColumn = (colIndex: number) => {
              selectedColumns.clear();
              selectedColumns.add(colIndex);
              handleRemoveColumnInternal(colIndex);
            };
          }
        });
      }
      
      return result;
    };
    
    // Use real data with checkboxes
    const { columnDefs, rowData } = customConvertToGridFormat(sourceData2D, true, false);
    
    const gridOptions = {
       columnDefs: columnDefs as any,
       rowData,
       domLayout: 'normal' as const,
       suppressCellFocus: false,
       headerHeight: 76,
       rowHeight: 34, // Set row height to 34px for source grid 
       autoSizeStrategy: {
         type: 'fitCellContents',
       },
       suppressColumnVirtualisation: true,
       suppressRowVirtualisation: true,
       suppressContextMenu: true,
       rowSelection: {
         mode: 'multiRow',
         checkboxes: true,
         headerCheckbox: true,
         enableClickSelection: true
       },
       components: {
         columnHeaderForSource: ColumnHeaderForSource
       },
       onGridReady: (params: any) => {
         sourceGridApi = params.api;
         sourceGridInitialized = true;
         if (onGridReady) {
           onGridReady(params.api);
         }
       },
       onSelectionChanged: (params: any) => {
         const selectedNodes = params.api.getSelectedNodes();
         const newSelectedRows = new Set<number>();
         
         selectedNodes.forEach((node: any) => {
           if (node.rowIndex !== null && node.rowIndex !== undefined) {
             newSelectedRows.add(node.rowIndex);
           }
         });
         
         selectedRows = newSelectedRows;
         if (onSelectionChanged) {
           onSelectionChanged(newSelectedRows);
         }
       },
       onCellValueChanged: (params: any) => {
         // Check if this is a grouper column (only for topics, not references)
         if (workingData && currentTableIndex >= 0 && effectiveTableId && !isReferenceMode && topicId && projectStructureStore) {
           const currentData = getCurrentVersion?.(workingData, currentTableIndex, effectiveTableId);
           if (currentData && currentData.columns) {
             const workingColumn = currentData.columns.find((col: any) => col.label === params.colDef.headerName || col.label === params.colDef.field);
             if (workingColumn?.key) {
               const topicStructure = projectStructureStore.topics?.find((t: any) => t.id === topicId);
               if (topicStructure) {
                 const tableStructure = topicStructure.tables?.find((t: any) => t.id === effectiveTableId);
                 if (tableStructure) {
                   const targetColumn = tableStructure.columns?.find((col: any) => col.id === workingColumn.key);
                   if (targetColumn && targetColumn.refTable === 'grouper') {
                     logWarning('Attempted to edit grouper column in source grid - this should not happen', "SourceTableEditor", undefined);
                     return; // Skip update for grouper columns
                   }
                 }
               }
             }
           }
         }
         
         hasChanges = true;
        
        // Update working data (this will set the isUpdatingCell flag)
         if (updateWorkingDataFromGrid) {
           updateWorkingDataFromGrid(params);
         }
        
        // Run validation manually without triggering reactive updates
        if (runValidation) {
          runValidation();
        }
       },
       defaultColDef: {
         editable: true,
         resizable: false,
         sortable: false,
         filter: false,
         suppressHeaderMenuButton: true,
         suppressHeaderContextMenu: true
       }
    };
    
    try {
      sourceGridInstance = createGrid(sourceGridDiv, gridOptions as any);
    } catch (error) {
      logError(error, "SourceTableEditor", undefined, { message: "Error creating source grid" });
    }
  }
  
  function handleSourceTableChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const selectedIndex = select.selectedIndex;
    if (selectedIndex >= 0 && selectedIndex < availableSourceTables.length) {
      const selectedTable = availableSourceTables[selectedIndex];
      if (onSourceTableChange) {
        onSourceTableChange(selectedTable);
      }
    }
  }
  
  // Refresh grid when data changes (but not during initialization)
  $: if (sourceGridApi && sourceData2D.length > 0 && sourceGridInitialized && convertToGridFormat && !isUpdatingCell) {
    // Create custom convertToGridFormat wrapper
    const customConvertToGridFormat = (data2D: any[][], includeCheckboxes: boolean, isTargetGrid: boolean) => {
      if (!convertToGridFormat) {
        return { columnDefs: [], rowData: [] };
      }
      
      const result = convertToGridFormat(data2D, includeCheckboxes, isTargetGrid);
      
      // Override onEditColumn and onDeleteColumn in columnDefs
      if (result.columnDefs) {
        result.columnDefs.forEach((colDef: any) => {
          if (colDef.headerComponentParams) {
            colDef.headerComponentParams.onEditColumn = (colIndex: number, event?: MouseEvent) => {
              selectedColumns.clear();
              selectedColumns.add(colIndex);
              openColumnPopup('edit', colIndex, event);
            };
            colDef.headerComponentParams.onDeleteColumn = (colIndex: number) => {
              selectedColumns.clear();
              selectedColumns.add(colIndex);
              handleRemoveColumnInternal(colIndex);
            };
          }
        });
      }
      
      return result;
    };
    
    const { columnDefs, rowData } = customConvertToGridFormat(sourceData2D, true, false);
    sourceGridApi.setGridOption('rowData', rowData);
    sourceGridApi.setGridOption('columnDefs', columnDefs);
  }
  
  // Expose refresh function to parent
  export function refreshGrid() {
    if (sourceGridApi && sourceData2D.length > 0 && convertToGridFormat) {
      // Create custom convertToGridFormat wrapper to inject popup handlers
      const customConvertToGridFormat = (data2D: any[][], includeCheckboxes: boolean, isTargetGrid: boolean) => {
        if (!convertToGridFormat) {
          return { columnDefs: [], rowData: [] };
        }
        
        const result = convertToGridFormat(data2D, includeCheckboxes, isTargetGrid);
        
        // Override onEditColumn and onDeleteColumn in columnDefs
        if (result.columnDefs) {
          result.columnDefs.forEach((colDef: any) => {
            if (colDef.headerComponentParams) {
              colDef.headerComponentParams.onEditColumn = (colIndex: number, event?: MouseEvent) => {
                selectedColumns.clear();
                selectedColumns.add(colIndex);
                openColumnPopup('edit', colIndex, event);
              };
              colDef.headerComponentParams.onDeleteColumn = (colIndex: number) => {
                selectedColumns.clear();
                selectedColumns.add(colIndex);
                handleRemoveColumnInternal(colIndex);
              };
            }
          });
        }
        
        return result;
      };
      
      const { columnDefs, rowData } = customConvertToGridFormat(sourceData2D, true, false);
      sourceGridApi.setGridOption('rowData', rowData);
      sourceGridApi.setGridOption('columnDefs', columnDefs);
    }
  }
  
  function getAvailableKeys(currentColumnKey: string = ''): string[] {
    // Handle reference mode
    if (isReferenceMode) {
      if (!projectStructureStore || !referenceId) return [];
      
      const referenceStructure = projectStructureStore.references?.find((r: any) => r.id === referenceId);
      if (!referenceStructure) return [];
      
      // Get all possible keys from the reference structure columns
      const allKeys: string[] = [];
      // Reference structure can have columns directly or in structure.columns
      const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
      columns.forEach((col: any) => {
        if (col.id) {
          allKeys.push(col.id);
        }
      });
      
      // Get currently used keys from working data (excluding the current column being edited)
      const usedKeys = new Set<string>();
      if (workingData && currentTableIndex >= 0 && getCurrentVersion) {
        const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
        if (currentData && (currentData as any).columns) {
          (currentData as any).columns.forEach((col: any, index: number) => {
            // Only exclude keys from other columns (not the current one being edited)
            if (col.key && col.key.trim() && index !== currentColumnIndex) {
              usedKeys.add(col.key);
            }
          });
        }
      }
      
      // Return available keys (excluding already used ones, but including the current column's key)
      return allKeys.filter(key => !usedKeys.has(key) || key === currentColumnKey);
    }
    
    // Topic mode (existing logic)
    if (!projectStructureStore || !topicId || !effectiveTableId) return [];
    
    // Find the target table structure
    const topicStructure = projectStructureStore.topics?.find((t: any) => t.id === topicId);
    if (!topicStructure) return [];
    
    const tableStructure = topicStructure.tables?.find((t: any) => t.id === effectiveTableId);
    if (!tableStructure) return [];
    
    // Get all possible keys from the target table structure
    const allKeys: string[] = [];
    tableStructure.columns?.forEach((col: any) => {
      if (col.refTable === 'grouper') {
        // For grouper references, use the grouper level ID as the key
        const grouperStructure = topicStructure.grouper;
        if (grouperStructure && grouperStructure.levels && grouperStructure.levels.length > 0) {
          // Use the first level's ID as the key (e.g., "group")
          const grouperLevel = grouperStructure.levels[0];
          allKeys.push(grouperLevel.id);
        }
      } else {
        // For regular columns, use the column ID directly
        allKeys.push(col.id);
      }
    });
    
      // Get currently used keys from working data (excluding the current column being edited)
      const usedKeys = new Set<string>();
      if (workingData && currentTableIndex >= 0 && getCurrentVersion) {
        const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
        if (currentData && (currentData as any).columns) {
          (currentData as any).columns.forEach((col: any, index: number) => {
            // Only exclude keys from other columns (not the current one being edited)
            if (col.key && col.key.trim() && index !== currentColumnIndex) {
              usedKeys.add(col.key);
            }
          });
        }
      }
      
      // Return available keys (excluding already used ones, but including the current column's key)
      // This ensures the current column's key is always available, even if it's already assigned
      const available = allKeys.filter(key => !usedKeys.has(key) || key === currentColumnKey);
      return available;
  }

  function openColumnPopup(mode: 'add' | 'edit', columnIndex: number, event?: MouseEvent) {
    try {
      popupMode = mode;
      currentColumnIndex = columnIndex;
      
      if (mode === 'add') {
        // Reset for add mode
        columnName = '';
        columnKey = '';
        // Get available keys for add mode (all unused keys)
        try {
          availableKeys = getAvailableKeys('');
        } catch (error) {
          logError(error, "SourceTableEditor", undefined, { message: "Error getting available keys for add mode" });
          availableKeys = [];
        }
      } else if (mode === 'edit' && columnIndex >= 0) {
        // Get current column data
        if (getCurrentVersion && workingData && currentTableIndex >= 0) {
          const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
          if (currentData && (currentData as any).columns && (currentData as any).columns[columnIndex]) {
            const column = (currentData as any).columns[columnIndex];
            columnName = column.label || '';
            columnKey = column.key || '';
            // Get available keys for edit mode (including current key)
            try {
              availableKeys = getAvailableKeys(columnKey);
            } catch (error) {
              logError(error, "SourceTableEditor", undefined, { message: "Error getting available keys for edit mode" });
              availableKeys = [];
            }
          } else {
            // Fallback to sourceData2D header
            columnName = sourceData2D[0]?.[columnIndex] || '';
            columnKey = '';
            try {
              availableKeys = getAvailableKeys('');
            } catch (error) {
              logError(error, "SourceTableEditor", undefined, { message: "Error getting available keys (fallback)" });
              availableKeys = [];
            }
          }
        } else {
          // Fallback to sourceData2D header
          columnName = sourceData2D[0]?.[columnIndex] || '';
          columnKey = '';
          try {
            availableKeys = getAvailableKeys('');
          } catch (error) {
            logError(error, "SourceTableEditor", undefined, { message: "Error getting available keys (fallback 2)" });
            availableKeys = [];
          }
        }
      }
      
      // Calculate popup position based on the clicked button
      if (event) {
        try {
          // Try to get the button element (might be the CustomImage or the Button)
          let targetElement = event.target as HTMLElement;
          // If clicking on CustomImage, try to find the parent button
          while (targetElement && targetElement.tagName !== 'BUTTON' && targetElement.parentElement) {
            targetElement = targetElement.parentElement;
          }
          const rect = targetElement.getBoundingClientRect();
          popupPosition = {
            top: rect.bottom + 5,
            left: rect.left - 8 // Offset 1rem (8px) to the left to align with button edge
          };
        } catch (error) {
          logError(error, "SourceTableEditor", undefined, { message: "Error calculating popup position" });
          // Fallback to center of viewport
          popupPosition = { 
            top: window.innerHeight / 2 - 100, 
            left: window.innerWidth / 2 - 150 
          };
        }
       } else {
        // Fallback position if no event provided
        popupPosition = { 
          top: window.innerHeight / 2 - 100, 
          left: window.innerWidth / 2 - 150 
        };
      }
      
      showColumnPopup = true;
    } catch (error) {
      logError(error, "SourceTableEditor", undefined, { message: "Error in openColumnPopup" });
      // Still try to open the popup even if there was an error
      showColumnPopup = true;
      availableKeys = [];
    }
  }
  
  function closeColumnPopup() {
    showColumnPopup = false;
    columnName = '';
    columnKey = '';
    currentColumnIndex = -1;
  }
  
  function saveColumnData() {
    if (!columnName.trim()) {
      alert('Please enter a column name');
      return;
    }
    
    if (onSaveColumn) {
      onSaveColumn(columnName.trim(), columnKey, popupMode, currentColumnIndex);
    }
    
    closeColumnPopup();
  }
  
  function handleRemoveColumnInternal(columnIndex: number) {
    if (onRemoveColumn) {
      onRemoveColumn(columnIndex);
    }
  }
  
  // Source grid operations - these operate on workingData and refresh sourceData2D
  function handleAddRowInternal() {
    if (workingData && currentTableIndex >= 0 && getCurrentVersion && effectiveTableId) {
      addRow(workingData, currentTableIndex, [], effectiveTableId);
      if (onSaveWorkingData) {
        onSaveWorkingData();
      }
      if (onRefreshSourceData) {
        onRefreshSourceData();
      }
    }
  }
  
  function handleRemoveRowInternal() {
    if (selectedRows.size > 0 && workingData && currentTableIndex >= 0 && getCurrentVersion && effectiveTableId) {
      const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
      if (currentData) {
        const rowIndex = Array.from(selectedRows)[0];
        const rowId = (currentData as any).rows[rowIndex]?.id;
        if (rowId) {
          removeRow(workingData, currentTableIndex, rowId, effectiveTableId);
          if (onSaveWorkingData) {
            onSaveWorkingData();
          }
          if (onRefreshSourceData) {
            onRefreshSourceData();
          }
          selectedRows.clear();
        }
      }
    }
  }
  
  function handlePivotTableInternal() {
    if (!workingData || currentTableIndex < 0 || !effectiveTableId) {
      logWarning('Cannot pivot: missing working data or table context', "SourceTableEditor", undefined);
      return;
    }
    
    // Call the pivot function from source-table-processing
    pivotTable(workingData, currentTableIndex, effectiveTableId);
    
    if (onSaveWorkingData) {
      onSaveWorkingData();
    }
    if (onRefreshSourceData) {
      onRefreshSourceData();
    }
  }
  
  function handleResetTableInternal() {
    if (workingData && currentTableIndex >= 0 && getCurrentVersion && effectiveTableId) {
      resetToInitial(workingData, currentTableIndex, effectiveTableId);
      if (onSaveWorkingData) {
        onSaveWorkingData();
      }
      if (onRefreshSourceData) {
        onRefreshSourceData();
      }
    }
  }

  onDestroy(() => {
    if (sourceGridInstance) {
      sourceGridInstance.destroy();
      sourceGridInstance = null;
      sourceGridApi = null;
    }
  });
</script>

{#if availableSourceTables.length > 0}
  <div class="source-section">
    <div class="section-header">
      <span class="section-label">SOURCE:</span>
      <select bind:value={selectedSourceTable} class="source-dropdown" on:change={handleSourceTableChange}>
        {#each availableSourceTables as table}
          <option value={table}>{table.name || table.table_name} (Page {table.page})</option>
        {/each}
      </select>
    </div>

    <div class="tool-controls">
      <div class="tool-group">
        <div class="tool-buttons">
          <Button class="tool-button" size="xs" color="light" title="Add Column" disabled={!columnAddEnabled} on:click={(e) => openColumnPopup('add', -1, e)}>
            <CustomImage id="table-column-add" size={24} />
          </Button>
          <Button class="tool-button" size="xs" color="light" title="Add Row" on:click={handleAddRowInternal}>
            <CustomImage id="table-row-add" size={24} />
          </Button>
          <Button class="tool-button" size="xs" color="light" title="Remove Row" disabled={!rowRemoveEnabled} on:click={handleRemoveRowInternal}>
            <CustomImage id="table-row-remove" size={24} />
          </Button>
        </div>
      </div>
      
      <div class="tool-group">
        <div class="tool-buttons">
          <Button class="tool-button" size="xs" color="light" title="Pivot Table" on:click={handlePivotTableInternal}>
            <CustomImage id="table-pivot" size={24} />
          </Button>
          <Button class="tool-button" size="xs" color="light" title="Reset Table" on:click={handleResetTableInternal}>
            <FontAwesomeIcon icon={faRotateLeft} size="lg" />
          </Button>
        </div>
      </div>
    </div>
    
    <div class="grid-wrapper">
      <div 
        bind:this={sourceGridDiv}
        class="ag-theme-quartz ag-source-grid"
        data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
      ></div>
    </div>
  </div>
  
  <!-- Replace Action Area -->
  <div class="replace-actions">
    <span class="replace-label">Replace:</span>
    <div class="replace-buttons">
      <Button size="xs" color="primary" disabled={!selectedButtonEnabled} on:click={onMatchSelected}>
        <FontAwesomeIcon icon={faArrowDown} class="mr-2" />
        Selected
      </Button>
      <span class="replace-label">OR</span>           
      <Button size="xs" color="primary" disabled={!matchingButtonEnabled} on:click={onMatchOnly}>
        <FontAwesomeIcon icon={faArrowDown} class="mr-2" />
        Matching
      </Button>
      <span class="replace-label">OR</span>           
      <Button size="xs" color="primary" disabled={!matchingButtonEnabled} on:click={onMatchAll}>
        <FontAwesomeIcon icon={faArrowDown} class="mr-2" />
        All
      </Button>
    </div>
  </div>
  
  <!-- Column Edit/Add Popup -->
  {#if showColumnPopup}
    <div 
      class="column-popup-overlay"
      on:click={closeColumnPopup}
      on:keydown={(e) => e.key === 'Escape' && closeColumnPopup()}
      role="button"
      tabindex="0"
      style="position: fixed !important; top: 0 !important; left: 0 !important; width: 100vw !important; height: 100vh !important; background-color: transparent !important; z-index: 999998 !important; display: flex !important; align-items: flex-start !important; justify-content: flex-start !important; padding: 20px !important;"
    >
      <div  class="column-popup"
        style="position: fixed !important; top: {popupPosition.top}px !important; left: {popupPosition.left}px !important;"
        on:click|stopPropagation
        on:keydown={(e) => e.key === 'Enter' && e.stopPropagation()}
        role="button"
        tabindex="0"
      >
        <div class="flex justify-between items-center mb-4">
          <h3 class="header">{popupMode === 'add' ? 'Add Column' : 'Edit Column'}</h3>
        </div>
        
        <div class="space-y-4">
          <div>
            <label class="label" for="column-name">Name</label>
            <input 
              type="text" 
              bind:value={columnName}
              class="input"
              id="column-name"
              placeholder="Column name"
            />
          </div>
          
          <div>
            <label class="label" for="column-key">Key</label>
            <select 
                bind:value={columnKey}
              class="select"
              id="column-key"
            >
              <option value="">-- None --</option>
              {#each availableKeys as key}
                <option value={key}>{key}</option>
              {/each}
            </select>
          </div>
        </div>
        
        <div class="flex justify-end space-x-2 gap-2 mt-4">
          <Button size="xs" color="primary" on:click={saveColumnData}>
              <FontAwesomeIcon icon={faSave} class="mr-2" />Save
          </Button>
          <Button size="xs" color="light" on:click={closeColumnPopup}>
              <FontAwesomeIcon icon={faTimes} class="mr-2" />Cancel
          </Button>
        </div>
      </div>
    </div>
  {/if}
{:else}
  <!-- No source tables available -->
  <div class="source-section">
    <div class="section-header">
      <span class="section-label">SOURCE:</span>
      <span class="table-name">No tables found</span>
    </div>
  </div>
{/if}
